<template>
  <ModalDialog
    :header-text="`Ingredient Submission Details Job Id: ${props.jobId}`"
    @close-modal="closeModal"
    open
  >
    <template v-slot:content>
      <div class="flex-container">
        <Grid
          class="ingredient-grid"
          :data-items="ingredients"
          :columns="columns"
          ref="ingredientDetailsGridRef"
          data-cy="ingredients-grid"
        >
          <template v-slot:AttachmentsCell="{ props: { dataItem } }">
            <td class="k-table-td">
              <a
                v-if="dataItem.Attachments?.length > 0"
                class="attachment-link"
                :title="`Download ${dataItem.Attachments.length} Attachments`"
                @click="downloadAttachment(dataItem)"
                >{{ `Download ${dataItem.Attachments.length} Attachments` }}</a
              >
            </td>
          </template>
        </Grid>
      </div>
    </template>
  </ModalDialog>
</template>

<script setup lang="ts">
import { api } from "@/api";
import { ref, onBeforeMount, onMounted } from "vue";
import { Grid } from "@progress/kendo-vue-grid";
import { useMainStore } from "@/stores/useMainStore";
import ModalDialog from "@/shared/ui/modal/ModalDialog.vue";
import { handleResponsePromiseAsync } from "@/shared/helpers/apiHelper";
import type { GridColumnPropsExtended } from "@/Domain/GridColumnPropsExtended";
import type { IngredientSubmissionDetail } from "@/Domain/IngredientSubmissionDetail";
import GridController from "@/shared/services/GridController";

const main = useMainStore();

const props = defineProps<{
  jobId: string;
  companyId?: string;
}>();

const emit = defineEmits(["closeModal"]);

const ingredients = ref<IngredientSubmissionDetail[]>([]);
const ingredientDetailsGridRef = ref<InstanceType<typeof Grid> | null>(null);

const columns = ref<GridColumnPropsExtended[]>([
  {
    title: "Ingredient",
    field: "Ingredient",
    minWidth: 80,
  },
  {
    title: "Brand",
    field: "Brand",
    minWidth: 80,
  },
  {
    title: "Source",
    field: "Source",
    minWidth: 80,
  },
  {
    title: "UKD Id",
    field: "UKDID",
    minWidth: 80,
  },
  {
    title: "Plant Name",
    field: "PlantName",
    minWidth: 80,
  },
  {
    title: "RMC",
    field: "rmc",
    minWidth: 80,
  },
  {
    title: "Status",
    field: "Status",
    minWidth: 80,
  },
  {
    title: "Attachments",
    cell: "AttachmentsCell",
    minWidth: 80,
  },
]);

const controller = new GridController<IngredientSubmissionDetail>(
  "SubmissionIngredientDetailsModal",
  columns.value
);

controller.initOptions();

const getIngredientSubmissionDetailsAsync = async function () {
  await handleResponsePromiseAsync(
    api.get("GetIngredientSubmissionDetails", {
      userLogin: main.userLogin,
      companyLogin: props.companyId ?? main.companyLogin,
      jobId: props.jobId,
      includeWorkflowDetails: false,
    }),
    (data) => {
      ingredients.value = data;
    },
    "ingredientDetailsModal.vue",
    "getIngredientSubmissionDetailsAsync"
  );
};

const downloadAttachment = function (ingredient: IngredientSubmissionDetail) {
  return api.getDownload("GetSubmissionDetailsAttachment", {
    jobId: props.jobId,
    inCartId: ingredient.IngredientInCartID.toString(),
    submissionType: "Ingredient",
  });
};

const closeModal = function () {
  emit("closeModal");
};

onBeforeMount(async () => {
  await getIngredientSubmissionDetailsAsync();
});

onMounted(() => {
  controller.setGridRef(ingredientDetailsGridRef);
});
</script>

<style scoped lang="scss">
.ingredient-grid {
  .attachment-link {
    cursor: pointer;
  }

  @include media-breakpoint-up(sm) {
    max-width: 90vw;
  }

  @include media-breakpoint-up(lg) {
    width: 800px;
  }

  @include media-breakpoint-up(xl) {
    width: 1100px;
  }
}

td {
  font-size: 13px;
}
</style>
